// Feature flag hook. Defaults from src/data/flags.json.
// Override layers, in order of precedence (later wins):
//   1. flags.json defaults
//   2. URL ?ff=name:1,name2:0 (per-session override for QA)
//   3. localStorage 'tn:flags:overrides' (sticky per-browser, used by /flags page)
//   4. window.__TN_FLAGS injected by an HTML script tag (server-rendered later)
//   5. Remote flag service (future) — will live behind the same hook
//
// `rollout` is an integer 0-100. When the flag is enabled, a deterministic
// hash of (flag-name + stable user-id-from-localStorage) decides whether
// the current visitor is in the bucket. Until accounts exist, the user-id
// is just a random UUID minted once per device.

import { useSyncExternalStore } from 'react';
import flagsConfig from '../data/flags.json';
import * as store from './localStore';

const OVERRIDES_KEY = 'flags:overrides';
const VISITOR_ID_KEY = 'flags:visitorId';

const isBrowser = typeof window !== 'undefined';

const generateVisitorId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `v-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const getVisitorId = () => {
  let id = store.get(VISITOR_ID_KEY);
  if (!id) {
    id = generateVisitorId();
    store.set(VISITOR_ID_KEY, id);
  }
  return id;
};

// FNV-1a 32-bit, ~uniform [0,1)
const hash01 = (str) => {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return ((h >>> 0) % 10000) / 10000;
};

const readUrlOverrides = () => {
  if (!isBrowser) return {};
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('ff');
    if (!raw) return {};
    const out = {};
    raw.split(',').forEach((pair) => {
      const [name, value] = pair.split(':');
      if (name) out[name.trim()] = value === '1' || value === 'on' || value === 'true';
    });
    return out;
  } catch (_e) {
    return {};
  }
};

const readLocalOverrides = () => store.get(OVERRIDES_KEY) || {};

const readWindowOverrides = () => {
  if (!isBrowser) return {};
  return (typeof window.__TN_FLAGS === 'object' && window.__TN_FLAGS) || {};
};

const resolveFlag = (name) => {
  const cfg = flagsConfig[name];
  if (!cfg || typeof cfg !== 'object') return false;

  // Layer overrides in increasing precedence
  const urlOverride = readUrlOverrides()[name];
  const localOverride = readLocalOverrides()[name];
  const windowOverride = readWindowOverrides()[name];

  if (typeof windowOverride === 'boolean') return windowOverride;
  if (typeof localOverride === 'boolean') return localOverride;
  if (typeof urlOverride === 'boolean') return urlOverride;

  if (!cfg.enabled) return false;
  const rollout = typeof cfg.rollout === 'number' ? cfg.rollout : 100;
  if (rollout >= 100) return true;
  if (rollout <= 0) return false;
  return hash01(`${name}:${getVisitorId()}`) * 100 < rollout;
};

// Hook with useSyncExternalStore so flag changes (e.g. from a debug panel)
// re-render subscribers. Today there's no subscribe path, so we ship a noop.
const subscribe = (callback) => {
  if (!isBrowser) return () => {};
  const handler = (e) => {
    if (!e || e.key === null || e.key === `tn:${OVERRIDES_KEY}`) callback();
  };
  window.addEventListener('storage', handler);
  window.addEventListener('tn:flags-changed', handler);
  return () => {
    window.removeEventListener('storage', handler);
    window.removeEventListener('tn:flags-changed', handler);
  };
};

export const useFlag = (name) => {
  return useSyncExternalStore(
    subscribe,
    () => resolveFlag(name),
    () => false, // SSR/SSG default — flags only ever opt-in on the client
  );
};

// Non-hook accessor for use outside React (effects, event handlers in plain JS).
export const isEnabled = (name) => resolveFlag(name);

// Helpers for a future debug/admin panel
export const setLocalOverride = (name, value) => {
  const overrides = readLocalOverrides();
  if (value === null || value === undefined) delete overrides[name];
  else overrides[name] = !!value;
  store.set(OVERRIDES_KEY, overrides);
  if (isBrowser) window.dispatchEvent(new Event('tn:flags-changed'));
};

export const clearOverrides = () => {
  store.remove(OVERRIDES_KEY);
  if (isBrowser) window.dispatchEvent(new Event('tn:flags-changed'));
};

export const listFlags = () =>
  Object.keys(flagsConfig).filter((k) => !k.startsWith('$')).map((name) => ({
    name,
    config: flagsConfig[name],
    resolved: resolveFlag(name),
  }));
